<template>
  <div>
    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <TabbedBox ref="tabBox" v-if="!loading && !loadingError">
      <template v-slot:tablist>
        <Tab :first="true" tabid="tab-pm-summary">
          <i class="far fa-tools"></i> Summary
        </Tab>
        <Tab tabid="tab-pm-production">
          <i class="far fa-tools"></i> Production
        </Tab>
        <Tab tabid="tab-pm-completed">
          <i class="far fa-check-circle"></i> Completed
        </Tab>
        <Tab tabid="tab-pm-performance-graphs">
          <i class="far fa-chart-line"></i> Station Performance
        </Tab>
      </template>

      <template v-slot:tabcontent>
        <TabPage :first="true" tabid="tab-pm-summary">
          <ContentTitle>Production Summary</ContentTitle>
          <ContentSubTitle>Summary of current production state. Ignores production items that are on-hold</ContentSubTitle>

          <AnalyticsDashboardValueRow>
            <div class="col">
              <AnalyticsDashboardValue 
                title="Panels Pending Production"
              >
                {{managerSummary.pendingPanelStats.totalPanelCount}}
                <template v-slot:info>{{managerSummary.pendingPanelStats.totalPanelCount}} Panels waiting to begin production with {{formatDecimal(managerSummary.pendingPanelStats.totalPanelArea, 2, true)}}m&sup2; total area</template>
              </AnalyticsDashboardValue>
            </div>
            <div class="col">
              <AnalyticsDashboardValue 
                title="Panels In Production"
              >
                {{managerSummary.productionPanelStats.totalPanelCount}}
                <template v-slot:info>{{managerSummary.productionPanelStats.totalPanelCount}} Panels in production with {{formatDecimal(managerSummary.productionPanelStats.totalPanelArea, 2, true)}}m&sup2; total area</template>
              </AnalyticsDashboardValue>
            </div>
            <div class="col">
              <AnalyticsDashboardValue 
                title="Panels In Despatch"
              >
                {{managerSummary.despatchPanelStats.totalPanelCount}}
                <template v-slot:info>{{managerSummary.despatchPanelStats.totalPanelCount}} Panels in despatch with {{formatDecimal(managerSummary.despatchPanelStats.totalPanelArea, 2, true)}}m&sup2; total area</template>
              </AnalyticsDashboardValue>
            </div>
          </AnalyticsDashboardValueRow>

          <ProductionStageSummaryWidget :embedded="true" />

          <DashboardTabbedContentBox :gridHeight="660" large>
            <template v-slot:tablist>
              <Tab :first="true" tabid="tab-summary-pending-stock">
                <i class="tabbedbox-tab-icon fas fa-inventory"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Pending Production</div>
                  <div class="tabbedbox-tab-subtitle">Panel Stock List</div>
                </div>
              </Tab>
              <Tab tabid="tab-summary-pending-thickness">
                <i class="tabbedbox-tab-icon fas fa-ruler"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Pending Production</div>
                  <div class="tabbedbox-tab-subtitle">Panel Thickness List</div>
                </div>
              </Tab>
              <Tab tabid="tab-summary-active-stock">
                <i class="tabbedbox-tab-icon fas fa-inventory"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Active Production</div>
                  <div class="tabbedbox-tab-subtitle">Panel Stock List</div>
                </div>
              </Tab>
              <Tab tabid="tab-summary-active-thickness">
                <i class="tabbedbox-tab-icon fas fa-ruler"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Active Production</div>
                  <div class="tabbedbox-tab-subtitle">Panel Thickness List</div>
                </div>
              </Tab>
              <Tab tabid="tab-summary-despatch-stock">
                <i class="tabbedbox-tab-icon fas fa-inventory"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Despatch</div>
                  <div class="tabbedbox-tab-subtitle">Panel Stock List</div>
                </div>
              </Tab>
              <Tab tabid="tab-summary-despatch-thickness">
                <i class="tabbedbox-tab-icon fas fa-ruler"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Despatch</div>
                  <div class="tabbedbox-tab-subtitle">Panel Thickness List</div>
                </div>
              </Tab>
              <Tab tabid="tab-summary-completed-stock">
                <i class="tabbedbox-tab-icon fas fa-inventory"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Completed</div>
                  <div class="tabbedbox-tab-subtitle">Panel Stock List</div>
                </div>
              </Tab>
              <Tab tabid="tab-summary-completed-thickness">
                <i class="tabbedbox-tab-icon fas fa-ruler"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Completed</div>
                  <div class="tabbedbox-tab-subtitle">Panel Thickness List</div>
                </div>
              </Tab>
              <Tab tabid="tab-summary-completed-stages">
                <i class="tabbedbox-tab-icon fas fa-check-circle"></i>
                <div class="tabbedbox-tab-info">
                  <div class="tabbedbox-tab-title">Completed</div>
                  <div class="tabbedbox-tab-subtitle">Stages Completed Today</div>
                </div>
              </Tab>
            </template>
            <template v-slot:tabcontent>
              <TabPage :first="true" tabid="tab-summary-pending-stock" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Panels Pending Production"
                  listNameTitle="Stock"
                  listValueTitle="Panel Area"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.pendingStockData.data"
                  :chartOptions="chartData.pendingStockData.options"
                />
              </TabPage>
              <TabPage tabid="tab-summary-pending-thickness" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Panels Pending Production"
                  listNameTitle="Thickness"
                  listValueTitle="Panel Area"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.pendingThicknessData.data"
                  :chartOptions="chartData.pendingThicknessData.options"
                />
              </TabPage>
              <TabPage tabid="tab-summary-active-stock" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Panels Currently In Production"
                  listNameTitle="Stock"
                  listValueTitle="Panel Area"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.productionStockData.data"
                  :chartOptions="chartData.productionStockData.options"
                />
              </TabPage>
              <TabPage tabid="tab-summary-active-thickness" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Panels Currently In Production"
                  listNameTitle="Thickness"
                  listValueTitle="Panel Area"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.productionThicknessData.data"
                  :chartOptions="chartData.productionThicknessData.options"
                />
              </TabPage>
              <TabPage tabid="tab-summary-despatch-stock" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Panels In Despatch"
                  listNameTitle="Stock"
                  listValueTitle="Panel Area"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.despatchStockData.data"
                  :chartOptions="chartData.despatchStockData.options"
                />
              </TabPage>
              <TabPage tabid="tab-summary-despatch-thickness" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Panels In Despatch"
                  listNameTitle="Thickness"
                  listValueTitle="Panel Area"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.despatchThicknessData.data"
                  :chartOptions="chartData.despatchThicknessData.options"
                />
              </TabPage>
              <TabPage tabid="tab-summary-completed-stock" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Panels Completed Production Today"
                  listNameTitle="Stock"
                  listValueTitle="Panel Area"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.completedStockData.data"
                  :chartOptions="chartData.completedStockData.options"
                />
              </TabPage>
              <TabPage tabid="tab-summary-completed-thickness" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Panels Completed Production Today"
                  listNameTitle="Stock"
                  listValueTitle="Panel Area"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.completedThicknessData.data"
                  :chartOptions="chartData.completedThicknessData.options"
                />
              </TabPage>

              <TabPage tabid="tab-summary-completed-stages" class="tabpage-padding-2">
                <AnalyticsListChartWidget
                  :embedded="true"
                  listTitle="Stages Completed Today"
                  listNameTitle="Stage"
                  listValueTitle="Panels"
                  :gridHeight="550"
                  :showList="true"
                  :showLegend="true"
                  :dataList="chartData.completedStageData.data"
                  :chartOptions="chartData.completedStageData.options"
                />
              </TabPage>
            </template>
          </DashboardTabbedContentBox>
        </TabPage>

        <TabPage tabid="tab-pm-production">
          <div class="row">
            <div class="col-6">
              <ContentTitle>Production</ContentTitle>
              <ContentSubTitle>List of panels in production that are due at the selected stage</ContentSubTitle>
            </div>
            <div class="col-6">
              <ReportFilterPopover
                class="float-right"
                headerTitle="In Production Filter"
                :showApplyButton="true"
                :showResetButton="true"
                @apply="applyInProductionFilter"
                @reset="resetInProductionFilter"
              >
                <template v-slot:popover>
                  <ReportFilterGroup label="Production Stage">
                    <FormSelect :options="productionStageLookup" v-model="editingInProductionFilter.productionStageId" />
                  </ReportFilterGroup>
                </template>
                <template v-slot:filterDisplay>
                  <div v-if="inProductionStageInfo">
                    <i class="far fa-wrench mr-2"></i> {{inProductionStageInfo.name}}
                  </div>
                </template>
              </ReportFilterPopover>
            </div>
          </div>
          <div v-if="inProductionStageInfo">
            <AnalyticsDashboardValueRow>
              <div class="col">
                <AnalyticsDashboardValue :title="`Panels Pending ${inProductionStageInfo.name}`">
                  {{inProductionStageInfo.productionStats.totalPanelCount}}
                  <template v-slot:info>{{inProductionStageInfo.productionStats.totalPanelCount}} Panels pending production with {{formatDecimal(inProductionStageInfo.productionStats.totalPanelArea, 2, true)}}m&sup2; total area</template>
                </AnalyticsDashboardValue>
              </div>
              <div class="col">
                <AnalyticsDashboardValue :title="`Panels Completed ${inProductionStageInfo.name} Today`">
                  {{inProductionStageInfo.completedStats.totalPanelCount}}
                  <template v-slot:info>{{inProductionStageInfo.completedStats.totalPanelCount}} Panels in despatch with {{formatDecimal(inProductionStageInfo.completedStats.totalPanelArea, 2, true)}}m&sup2; total area</template>
                </AnalyticsDashboardValue>
              </div>
            </AnalyticsDashboardValueRow>

            <FormHorizontalRule />
            
            <ProductionManagerPanelDataTable
              ref="pendingPanelDataTable"
              v-model="pendingPanelSelectedIdList"
              :productionStage="inProductionStageInfo"
              :showPendingStage="true">
              <template v-slot:title>
                <ContentTitle>Panels Due At {{inProductionStageInfo.name}}</ContentTitle>
              </template>
              <template v-slot:tools>
                <DropdownMenu title="Actions" icon="fa-tools" buttonClass="btn-med btn-primary" v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionManager)">
                  <DropdownMenuItem icon="fa-exchange" @click="rerouteSelectedPanels(inProductionStageInfo)">Re-route Selected Panels</DropdownMenuItem>
                </DropdownMenu>

              </template>
            </ProductionManagerPanelDataTable>
          </div>
        </TabPage>

        <TabPage tabid="tab-pm-completed">
          <div class="row">
            <div class="col-6">
              <ContentTitle>Completed Stage</ContentTitle>
              <ContentSubTitle>List of panels that completed the selected stage during the date range</ContentSubTitle>
            </div>
            <div class="col-6">
              <ReportFilterPopover
                class="float-right"
                headerTitle="Completed Filter"
                :showApplyButton="true"
                :showResetButton="true"
                @apply="applyCompletedFilter"
                @reset="resetCompletedFilter"
              >
                <template v-slot:popover>
                  <ReportFilterGroup label="Production Stage">
                    <FormSelect :options="productionStageLookup" v-model="editingCompletedFilter.productionStageId" />
                  </ReportFilterGroup>
                  <ReportFilterGroup label="Completed Date Filter">
                    <ReportDateRangeSelect
                      :startDate="editingCompletedFilter.startDate"
                      :endDate="editingCompletedFilter.endDate"
                      @updateStartDate="editingCompletedFilter.startDate = $event"
                      @updateEndDate="editingCompletedFilter.endDate = $event"
                    />
                  </ReportFilterGroup>
                </template>
                <template v-slot:filterDisplay>
                  <div v-if="completedStageInfo">
                    <i class="far fa-wrench mr-2"></i> {{completedStageInfo.name}}
                  </div>
                  <div v-if="appliedCompletedFilter.startDate || appliedCompletedFilter.endDate">
                    <i class="far fa-calendar mr-2"></i>{{appliedCompletedFilter.startDate | formatDate}} - {{appliedCompletedFilter.endDate | formatDate}}
                  </div>
                </template>
              </ReportFilterPopover>
            </div>
          </div>
          <div v-if="completedStageInfo">
            <ProductionManagerPanelDataTable
              :productionStage="completedStageInfo"
              :showCompletedStage="true"
              :selectionEnabled="false"
              :completedStartDate="appliedCompletedFilter.startDate"
              :completedEndDate="appliedCompletedFilter.endDate">
              <template v-slot:title>
                <ContentTitle>Panels Completed {{completedStageInfo.name}}</ContentTitle>
                <ContentSubTitle>Panels that completed {{completedStageInfo.name}} between {{formatDate(appliedCompletedFilter.startDate)}} and {{formatDate(appliedCompletedFilter.endDate)}}</ContentSubTitle>
              </template>
            </ProductionManagerPanelDataTable>
          </div>
        </TabPage>

        <TabPage tabid="tab-pm-performance-graphs">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Station Performance For Today</ContentTitle>
          </div>
        </div>
        <div class="row no-gutter">
          <div class="col-12">
            <div class="station-performance-graph-container row no-gutters">
              <template v-for="station of stationList">
                <div class="graph-container col-3 float-left" :key="station.id" v-if="station.showStationGraph">
                  <h2 class="graph-title text-truncate">{{stationStageName(station)}} - {{station.name}}</h2>
                  <StationPerformanceGraph :productionStation="station" />
                </div>
              </template>
            </div>
          </div>
        </div>
      </TabPage>
      </template>
    </TabbedBox>
  </div>
</template>

<style scoped>
.graph-title {
  font-size: 18px;
  margin-top: 0px;
  margin-bottom: 5px;
}
.graph-container {
  padding: 10px;
  margin-bottom: 8px;
}
</style>


<script>
import _ from 'lodash'
import FormMixin from '@/mixins/form'
import Utils from '@/utils/utils'

import DashboardTabbedContentBox from '@/components/dashboard/dashboardTabbedContentBox'
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import ChartDoughnut from '@/components/charts/chartDoughnut'
import AnalyticsListChartWidget from '@/modules/intelligence/components/analyticsListChartWidget'
import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ProductionStageSummaryWidget from '@/modules/intelligence/dashboardWidgets/productionStageSummaryWidget'
import StationPerformanceGraph from '@/apps/glass-production-station/src/components/stationPerformanceGraph'
import ProductionManagerPanelDataTable from '@/modules/production/components/productionManagerPanelDataTable'
import ProductionManagerRerouteDialog from '@/modules/production/views/productionManagerRerouteDialog'

import { UOMTypeUnitsLookup } from '@/store/types'

export default {
  name: 'ProductionManager',
  mixins: [FormMixin],
  components: {
    DashboardTabbedContentBox,
    AnalyticsWidget,
    AnalyticsDashboardValue,
    AnalyticsDashboardValueRow,
    ChartDoughnut,
    AnalyticsListChartWidget,
    StationPerformanceGraph,
    ProductionManagerPanelDataTable,
    ProductionStageSummaryWidget,
    ReportFilterPopover,
    ReportFilterGroup,
    ReportDateRangeSelect
  },
  data() {
    return {
      loading: true,
      loadingError: '',
      chartWidgetHeight: 400,
      productionStages: [],
      stationList: [],
      pendingPanelSelectedIdList: [],
      managerSummary: {},
      chartData: {},

      defaultInProductionFilter: {
        productionStageId: 0
      },
      editingInProductionFilter: {},
      appliedInProductionFilter: {},

      defaultCompletedFilter: {
        productionStageId: 0,
        startDate: moment().local().startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
      },
      editingCompletedFilter: {},
      appliedCompletedFilter: {}
    }
  },
  computed: {
    activeProductionStageList() {
      // Get all stages with active panels in production or completed today
      return this.managerSummary.stageSummaryList.filter(stageInfo => {
        return stageInfo.productionStats.totalPanelCount > 0 || stageInfo.completedStats.totalPanelCount > 0
      })
    },
    productionStageLookup() {
      return this.productionStages.map(productionStage => {
        return {
          value: productionStage.id,
          name: productionStage.name
        }
      })
    },
    inProductionStageInfo() {
      return this.managerSummary.stageSummaryList.find(stage => stage.id == this.appliedInProductionFilter.productionStageId)
    },
    completedStageInfo() {
      return this.managerSummary.stageSummaryList.find(stage => stage.id == this.appliedCompletedFilter.productionStageId)
    }
  },
  mounted() {
    this.editingInProductionFilter = _.clone(this.defaultInProductionFilter)
    this.appliedInProductionFilter = _.clone(this.defaultInProductionFilter)

    this.editingCompletedFilter = _.clone(this.defaultCompletedFilter)
    this.appliedCompletedFilter = _.clone(this.defaultCompletedFilter)

    this.requestData()
  },
  methods: {
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.productionService.getProductionStageList(),
        this.$apiService.productionService.getProductionManagerSummary(),
        this.$apiService.productionService.getProductionStationList()
      ])
      .then(results => {
        this.productionStages = results[0].data.filter(stage => stage.isActive)
        this.managerSummary = results[1].data
        this.stationList = results[2].data

        // Select default stage
        if(this.productionStages.length > 0) {
          this.defaultInProductionFilter.productionStageId = this.productionStages[0].id
          this.defaultCompletedFilter.productionStageId = this.productionStages[0].id
          this.resetInProductionFilter();
          this.resetCompletedFilter();
        }

        // Build the chart data
        this.buildChartData()
      })
      .catch(err => {
        console.error(err)
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },
    buildChartData() {
      var _buildChartInfo = (list, seriesName) => {
        let options = {
          tooltip: {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${Utils.formatDecimal(param.value, 1, true)}m&sup2; / ${param.data.count} Panels - (${param.percent}%)`
            }
          }
        }
        let data = list.map(group => {
          return {
            name: group.name,
            value: Utils.roundNumber(group.panelArea),
            displayValue: Utils.formatDecimal(group.panelArea, 1, true) + `m&sup2;` + ` (${group.panelCount} Panels)`,
            count: group.panelCount
          }
        })

        return {
          options: options,
          data: data
        }
      }
      this.chartData.pendingStockData = _buildChartInfo(this.managerSummary.pendingPanelStats.stockList, 'Pending Production');
      this.chartData.pendingThicknessData = _buildChartInfo(this.managerSummary.pendingPanelStats.thicknessList, 'Pending Production');
      this.chartData.productionStockData = _buildChartInfo(this.managerSummary.productionPanelStats.stockList, 'In Production');
      this.chartData.productionThicknessData = _buildChartInfo(this.managerSummary.productionPanelStats.thicknessList, 'In Production');
      this.chartData.despatchStockData = _buildChartInfo(this.managerSummary.despatchPanelStats.stockList, 'In Despatch');
      this.chartData.despatchThicknessData = _buildChartInfo(this.managerSummary.despatchPanelStats.thicknessList, 'In Despatch');
      this.chartData.completedStockData = _buildChartInfo(this.managerSummary.completedProductionPanelStats.stockList, 'Completed Production');
      this.chartData.completedThicknessData = _buildChartInfo(this.managerSummary.completedProductionPanelStats.thicknessList, 'Completed Production');


      // Stages
      var _buildStageChartInfo = (list, seriesName) => {
        let options = {
          tooltip: {
            formatter(param) {
              return `${param.seriesName} <br />${param.name} : ${param.data.count} Panels / ${Utils.roundNumber(param.data.stageUnits, 1, true)} ${param.data.uomSuffix} - (${param.percent}%)`
            }
          }
        }
        let data = _.compact(list.map(group => {
          let uomSuffix = this.formatLookupName(group.uomType, UOMTypeUnitsLookup)

          return {
            name: group.name,
            value: group.panelCount,
            stageUnits: group.stageUnits,
            uomSuffix: uomSuffix,
            displayValue: `${group.panelCount} Panels (${Utils.roundNumber(group.stageUnits, 1, true) + ' ' + uomSuffix})`,
            count: group.panelCount
          }
        }))

        return {
          options: options,
          data: data
        }
      }

      // TODO: Stages Completed today
      this.chartData.completedStageData = _buildStageChartInfo(this.managerSummary.completedStageStats.stageList, 'Completed Stages');

    },
    rerouteSelectedPanels(productionStage) {
      this.displayModal(ProductionManagerRerouteDialog, {
        productionStage: productionStage,
        panelIdList: this.pendingPanelSelectedIdList
      })
      .then(result => {
        this.successToast('Panel Routing Updated')
        this.$refs.pendingPanelDataTable.reload()
      })
    },
    stationStageName(station) {
      if(station.productionStages && station.productionStages.length > 0) {
        return station.productionStages[0].name
      }
      return 'None'
    },
    applyInProductionFilter() {
      this.appliedInProductionFilter = _.clone(this.editingInProductionFilter)
    },
    resetInProductionFilter() {
      this.editingInProductionFilter = _.clone(this.defaultInProductionFilter)
      this.appliedInProductionFilter = _.clone(this.defaultInProductionFilter)
    },
    applyCompletedFilter() {
      this.appliedCompletedFilter = _.clone(this.editingCompletedFilter)
    },
    resetCompletedFilter() {
      this.editingCompletedFilter = _.clone(this.defaultCompletedFilter)
      this.appliedCompletedFilter = _.clone(this.defaultCompletedFilter)
    }
  }
}
</script>